import { parseApiError } from './apiError';
import { RentalsApi } from '.';

const api = new RentalsApi();

export const getAllRentals = (token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiRentalsGet().then(res => res.data);
};

export const getRentalsByOwner = (ownerId: string, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals?owner_id=${ownerId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async res => {
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    });
  }
  return api.apiRentalsGet().then(res => res.data).then(rentals =>
    rentals.filter((rental: any) => rental.owner_id === ownerId)
  );
};

export const getRentalsByRenter = (renterId: string, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals?renter_id=${renterId}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async res => {
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    });
  }
  return api.apiRentalsGet().then(res => res.data).then(rentals =>
    rentals.filter((rental: any) => rental.renter_id === renterId)
  );
};

export const getRentalsByStatus = (status: 'pending' | 'active' | 'completed' | 'cancelled', token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals?status=${status}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiRentalsGet(status, undefined).then(res => res.data);
};

export const getRentalById = (id: string, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals/${id}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiRentalsIdGet(id).then(res => res.data);
};

export const createRental = (data: any, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(async res => {
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    });
  }
  return api.apiRentalsPost(data).then(res => res.data);
};

export const updateRental = (id: string, data: any, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals/${id}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }).then(res => res.json());
  }
  return api.apiRentalsIdPut(id, data).then(res => res.data);
};

export const deleteRental = (id: string, token?: string) => {
  if (token) {
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals/${id}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiRentalsIdDelete(id).then(res => res.data);
};

export const cancelRental = async (id: string, cancellationReason: string, token: string) => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/rentals/${id}/cancel`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ cancellation_reason: cancellationReason })
  });
  if (!res.ok) throw await parseApiError(res);
  return res.json();
};

export const getRentalsCount = (renterId?: string, ownerId?: string, status?: 'pending' | 'active' | 'completed' | 'cancelled', token?: string) => {
  if (token) {
    const params = new URLSearchParams();
    if (renterId) params.append('renter_id', renterId);
    if (ownerId) params.append('owner_id', ownerId);
    if (status) params.append('status', status);
    
    return fetch(`${process.env.REACT_APP_API_URL}api/rentals/count?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(res => res.json());
  }
  return api.apiRentalsCountGet(renterId, ownerId, status).then(res => res.data);
};

// Nouvelle fonction pour récupérer les créneaux occupés d'un véhicule
export const getVehicleOccupiedSlots = (vehicleId: string, startDate?: string, endDate?: string, token?: string) => {
  if (token) {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const url = `${process.env.REACT_APP_API_URL}api/rentals/vehicle/${vehicleId}/occupied-slots${params.toString() ? `?${params.toString()}` : ''}`;
    
    return fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }).then(async res => {
      if (!res.ok) throw await parseApiError(res);
      return res.json();
    });
  }

  // Note: Cette fonction n'existe peut-être pas dans l'API générée, donc on utilise fetch par défaut
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  
  const url = `${process.env.REACT_APP_API_URL}api/rentals/vehicle/${vehicleId}/occupied-slots${params.toString() ? `?${params.toString()}` : ''}`;
  
  return fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json'
    }
  }).then(async res => {
    if (!res.ok) throw await parseApiError(res);
    return res.json();
  });
};

// L'équipe retire une absence reprochée à tort (contestation tranchée) : la
// location reste annulée, le compte n'est plus compté. Admin seulement.
export const clearNoShow = async (rentalId: string, token: string): Promise<{ absent_user_id: string; absent_no_show_count: number }> => {
  const res = await fetch(`${process.env.REACT_APP_API_URL}api/rentals/${rentalId}/no-show`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${token}` }
  });
  if (!res.ok) throw await parseApiError(res, "Retrait de l'absence impossible");
  return res.json();
};
